import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { shareOnMobile } from "react-mobile-share";
import { Input, Combobox, Textarea, GoogleInput } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { Callout } from "@/components/ui/callout";
import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import {
    PencilIcon,
    LockClosedIcon,
    ShareIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/16/solid";
import { PhoneIcon } from "@heroicons/react/24/solid";
import avatarImg from "@/assets/default_pfp.jpg";
import { CredentiallyCard } from "./credentially-card";
import useModal from "@/hooks/use-modal";
const CropImageModal = lazy(() =>
    import("./crop-image").then((module) => ({
        default: module.CropImageModal,
    })),
);

export const ClinicianForm = ({
    methods,
    clinician,
    onSubmit,
    handleProfilePic,
    preview,
    previewError,
    handleChange,
    aboutTxtLimit,
    professions,
    selectedProfessionTxt,
    mutation,
    onResend,
}) => {
    const cropImageModal = useModal();

    const userName = clinician
        ? [clinician.firstName, clinician.lastName].filter(Boolean).join(" ")
        : "";

    const userEmail = clinician?.email ?? "";

    // Show resend credentially button if already sent once
    const showResend = !!clinician?.credentially_OK;
    const isResend = mutation.variables?.credentially_OK === 2;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-12 gap-4">
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Basic details
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full grid grid-cols-subgrid gap-x-1 gap-y-4 md:col-span-4 lg:col-span-2">
                            <div className="col-span-full flex flex-col items-center gap-x-1 gap-y-2 md:gap-x-4">
                                <p className="mb-1 block w-fit text-sm/6 font-medium text-darkgreen-500">
                                    Profile picture
                                </p>
                                <div
                                    className="group relative size-40 cursor-pointer"
                                    onClick={() => cropImageModal.open()}
                                >
                                    <div className="aspect-square w-full max-w-40 rounded-full bg-gray-100 object-cover">
                                        <img
                                            src={
                                                clinician?.profileImageUrl ||
                                                avatarImg
                                            }
                                            alt="Profile Picture"
                                            className="rounded-full"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="pointer-events-none absolute right-0 bottom-2 rounded-full px-2.5 py-1"
                                        >
                                            <PencilIcon className="relative top-0.75 size-3.5! shrink-0" />
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                                <div className="w-full md:w-auto">
                                    <Input
                                        type="file"
                                        name="profilePic"
                                        label="Profile picture"
                                        hideLabel={true}
                                        accept="image/jpeg, image/png"
                                        size="sm"
                                        onChange={handleChange}
                                        className="hidden"
                                    />
                                    {previewError && (
                                        <Callout
                                            variant="danger"
                                            size="sm"
                                            icon
                                            className="mt-2"
                                            role="alert"
                                        >
                                            Image must be JPG or PNG
                                        </Callout>
                                    )}
                                </div>
                                <div className="text-center text-sm">
                                    {userName != " " && <p>{userName}</p>}
                                    <p>{userEmail}</p>
                                </div>
                                <div>
                                    <Button
                                        asChild
                                        type="button"
                                        size="sm"
                                        className="mt-2"
                                    >
                                        <Link to="/clinician/manage/account">
                                            <LockClosedIcon className="shrink-0" />
                                            Edit Email/Password
                                        </Link>
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        className="mt-2 mb-4 w-full"
                                        onClick={() =>
                                            shareOnMobile({
                                                title: `${clinician.firstName} ${clinician.lastName} - ${professions[clinician.professionGuid].title}`,
                                                url: `https://portal.welnido.com/clinicians/view/${clinician.firstName}-${clinician.lastName}/${clinician.userID}`,
                                                text: `Check out ${clinician.firstName} ${clinician.lastName} on Welnido.`,
                                                // images: [imgBase64]
                                            })
                                        }
                                    >
                                        <ShareIcon />
                                        Share Profile
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid grid-cols-subgrid gap-x-1 gap-y-4 md:col-start-6 md:col-end-12 md:gap-x-4">
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="firstName"
                                    label="First name"
                                    placeholder="First name"
                                    autoComplete="given-name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="lastName"
                                    label="Last name"
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="dateOfBirth"
                                    label="Date of birth"
                                    placeholder="Date of birth"
                                    type="date"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="phoneNumber"
                                    label="Phone number"
                                    placeholder="Phone number"
                                    icon={PhoneIcon}
                                />
                            </div>
                            <div className="col-span-full">
                                <Textarea
                                    name="about"
                                    label="About me"
                                    placeholder="About me"
                                    rows={5}
                                />
                                <Callout
                                    variant="info"
                                    icon
                                    className="mt-2"
                                    size="sm"
                                >
                                    <p>
                                        Tell patients who you are, what you
                                        treat, and how you work. A few short
                                        paragraphs is plenty.
                                    </p>
                                </Callout>
                                <Callout
                                    variant="warning"
                                    icon
                                    className="mt-2"
                                    size="sm"
                                >
                                    <p>
                                        {2500 - aboutTxtLimit} characters
                                        remaining
                                    </p>
                                </Callout>
                            </div>
                        </div>
                    </Card>
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Personal address
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid gap-4 lg:col-span-6">
                            <div className="col-span-full grid grid-cols-subgrid items-end">
                                <div className="col-span-full md:col-span-6 lg:col-span-3">
                                    <GoogleInput
                                        name="findAddress"
                                        label="Find Address"
                                        placeholder="Find Address"
                                        setValue={methods.setValue}
                                    />
                                </div>
                                <div className="col-span-full mt-2 md:col-span-6 md:mt-0 lg:col-span-3">
                                    <Callout variant="info" size="sm" icon>
                                        <p>
                                            Your home or correspondence address,
                                            used for verification only and never
                                            shown to patients.
                                        </p>
                                    </Callout>
                                </div>
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressNumber"
                                    label="House number/name"
                                    placeholder="House number/name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressLine1"
                                    label="First line"
                                    placeholder="First line"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressLine2"
                                    label="Second line"
                                    placeholder="Second line"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressTown"
                                    label="Town"
                                    placeholder="Town"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressCounty"
                                    label="County"
                                    placeholder="County"
                                    autoComplete="address-level1"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressPostalCode"
                                    label="Postcode"
                                    placeholder="Postcode"
                                />
                            </div>
                            <div className="hidden">
                                <Input name="addressLatitude" type="hidden" />
                                <Input name="addressLongitude" type="hidden" />
                            </div>
                        </div>
                    </Card>
                    <div className="col-span-full xl:col-span-4">
                        <CredentiallyCard
                            showResend={showResend}
                            methods={methods}
                            onSubmit={onResend}
                            mutation={mutation}
                        />
                    </div>
                    <Card size="lg" className="grid-cols-12 xl:col-span-8">
                        <div className="col-span-full grid grid-cols-subgrid md:col-span-2 lg:mr-8 xl:col-span-3">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Profession
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-6 gap-4 lg:col-span-9">
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Combobox
                                    name="professionGuid"
                                    label="Profession"
                                    placeholder="Profession"
                                >
                                    {professions.map((p) => (
                                        <option
                                            key={p.typeGuid}
                                            value={p.typeGuid}
                                        >
                                            {p.title}
                                        </option>
                                    ))}
                                </Combobox>
                            </div>
                            <div className="col-span-full flex flex-col md:col-span-6 lg:col-span-3">
                                <p className="mb-1 text-sm/6 font-medium">
                                    Statutory regulatory / Governing Body{" "}
                                </p>
                                <p className="my-auto">
                                    {selectedProfessionTxt}
                                </p>
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="pin"
                                    label="Pin"
                                    placeholder="Pin"
                                />
                            </div>
                            <div className="col-span-full">
                                <Callout variant="info" icon size="sm">
                                    <p className="mb-2">
                                        Select your profession, then enter your
                                        registration number (e.g. NMC PIN, GMC
                                        number, HCPC number).
                                    </p>
                                    <p>
                                        <i>
                                            If your profession isn't statutorily
                                            regulated, you can enter a voluntary
                                            register number instead, or leave
                                            this blank and continue.
                                        </i>
                                    </p>
                                </Callout>
                            </div>
                            <div className="col-span-full flex gap-3">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-fit"
                                    disabled={mutation.isPending}
                                >
                                    <CheckIcon className="stroke-2" />
                                    {mutation.isPending && !isResend
                                        ? "Saving..."
                                        : "Save"}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
                <Suspense fallback={null}>
                    <CropImageModal
                        isOpen={cropImageModal.isOpen}
                        onClose={() => cropImageModal.close()}
                        onClick={handleProfilePic}
                        preview={preview}
                        clinicianId={clinician.userID}
                    />
                </Suspense>
            </form>
        </FormProvider>
    );
};
